/** lag-rangedlist
 * 
 *  A listing that has a range (start-end) input at the left and an amount (and isPercent switch) to the right of it.
 *  It provides functionality to add and remove rows as needed.
 *
 *  PROPERTIES/METHODS:
 *      lag-rangedlist {
 *          disabled: Boolean, specifies if the control is disabled (not-editable) or not (editable/normal).
 *          label: String, heading label text.
 *          labelFineprint: String, optional fineprint text next to main label.
 *          ranges: Array of objects containing range data as follows...
 *              [{
 *                  start: The start boundary of the range (inclusive), must be a number, cannot be negative, should not be null (null indicates invalid).
 *                  end: The ending boundary of the range (exclusive), must be a number or null (null indicates infinity), cannot be below start.
 *                  amount: The amount applicable to the range, must be a number.
 *                  isPercent: The amount value should be considered as a percentage value.
 *              },
 *              ...]
 *
 *          addRange(indexToAddAt, start, end, amount, isPercent)
 *          delRange(indexToDelete)
 *      }
 *
 *  EVENTS:
 *      'adding': User has clicked add-row, this is cancelable. The event.detail contains: { index }
 *      'deleting': User has clicked delete-row, this is cancelable. The event.detail contains: { index }
 *      'change': Ranged values have changed (or one has been added/deleted). The event.detail may contain the following data (with appropriate type):
 *                {
 *                  index,
 *                  type: 'added', 'deleted', 'range-start', 'range-end', 'amount', 'isPercent',
 *                  data: { start, end, amount, isPercent }
 *                }
 *
 *  HTML:
 *      <lag-rangedlist ranges="[[theRangesArray]]"></lag-rangedlist>
 *
 *  JS:
 *      theLagRangedList.init(theRangesArray);
 *      // or
 *      theLagRangedList.ranges = theRangesArray;
 *
 *  STYLING:
 *      --lag-rangedlist-inputBorder (border)
 *      --lag-rangedlist-invalidColor (color)
 *      --lag-rangedlist-fadedColor (color)
 *      --lag-rangedlist-percentInactiveBgColor (color)
 *      --lag-rangedlist-percentInactiveColor (color)
 *      --lag-rangedlist-percentActiveColor (color)
 *      --lag-rangedlist-percentActiveBgColor (color)
 *      --lag-rangedlist-linkColor (color)
 *      --lag-rangedlist-linkHoverColor (color)
 *      --lag-rangedlist-deleteColor (color)
 *      --lag-rangedlist-adderColor (color)
 *      --lag-rangedlist-adderBgColor (color)
 *      --lag-rangedlist-adderHoverColor (color)
 *      --lag-rangedlist-adderHoverColor_first (color)
 *      --lag-rangedlist-adderHoverColor_between (color)
 *      --lag-rangedlist-adderHoverColor_last (color)
 */

import { PolymerElement } from '@polymer/polymer/polymer-element.js';
import { templatize } from '@polymer/polymer/lib/utils/templatize.js';

import '@polymer/iron-icon/iron-icon.js';
import '../../assets/icons.js';

import { defineElement } from '../../utility/defineElement.js';
import { debounce } from '../../utility/debounce.js';

import { template } from './lag-rangedlist.html.js';

const FORMAT_PRECISION = 4;
const ROUND_PRECISION = 1e4; // 1000
const RANGE_SPREAD_DEFAULT = 100;
const RANGE_AMOUNT_DEFAULT = 0;
const RANGE_ISPERCENT_DEFAULT = true;

const CLASS_ROW_PERCENT_ACTIVE = 'row-ispercent--active';

// TODO: Could consider allowing overlapping rates if of different types (eg 2% and $3000) ???

defineElement(class extends PolymerElement {

    static get is() { return 'lag-rangedlist'; }

    static get template() { return template; }

    static get properties() {
        return {
            ranges: {
                type: Array,
                value: [],
                observer: '_observeRanges'
            },
            label: {
                type: String,
                value: null
            },
            labelFineprint: {
                type: String,
                value: null
            },
            disabled: {
                type: Boolean,
                value: false,
                notify: true,
                reflectToAttribute: true, // For use in CSS
                observer: '_observeDisabled'
            }
        };
    }

    constructor() {
        super();

        this._cache = {}; // Used to cache template stamps.
    }

    connectedCallback() {
        super.connectedCallback();

        this.$.listing.addEventListener('click', (ev) => this._handleClick(ev));
        this.$.listing.addEventListener('change', (ev) => this._handleChange(ev));
    }

    // -- OBSERVE & COMPUTE --

    _observeRanges(ranges, oldRanges) {
        if (ranges === oldRanges)
            return; // Value didn't change, don't re-init.

        this.init(ranges);
    }

    _observeDisabled(disabled, oldDisabled) {
        if (typeof(disabled) !== 'boolean' || disabled === oldDisabled)
            return; // Invalid type or didn't change.

        this._setDisabled(disabled);
    }

    // -- PUBLIC METHODS --

    /** init()
     * Initialize the lag-rangedlist with whatever ranges have been specified and whatever <template> has been provided.
     */
    init(ranges) {
        if (!ranges || !(ranges instanceof Array)) {
            // If no ranges then set as an empty array.
            this.ranges = [];
            return; // Exist since the setting this.ranges will trigger the observer and re-call this method, don't want infinite recursion.
        }

        // Validate and normalize the ranges so they are predictable and reasonable.
        this._validateAndNormalizeRanges(ranges);

        // Debounce just in-case the ranges property/observer is repeatedly assigned to for whatever reason.
        this._debouncer_init = debounce(this._debouncer_init, 200)
            .then(_ => this._applyRangesToUI(ranges)); // Only execute the very last debounce invocation (for the debounce period).
    }

    addRange(indexToAddAt, start, end, amount, isPercent) {
        // If null/undefined/invalid then default to new row at end.
        if (indexToAddAt !== 0 && (indexToAddAt < 0 || indexToAddAt > this.ranges.length))
            indexToAddAt = this.ranges.length;

        // Insert the new range and then apply to UI.
        this.ranges.splice(indexToAddAt, 0, {
            start: start || 0,
            end: end || null,
            amount: (typeof amount === 'number' && amount >= 0) ? amount : RANGE_AMOUNT_DEFAULT,
            isPercent: (isPercent || isPercent === false) ? !!isPercent : RANGE_ISPERCENT_DEFAULT
        });

        this._validateAndNormalizeRanges(this.ranges);
        this._applyRangesToUI(this.ranges);

        this._dispatchChangeEvent('added', indexToAddAt, this.ranges[indexToAddAt]);
    }
    delRange(indexToDelete) {
        if (!this.ranges.length || indexToDelete < 0 || indexToDelete >= this.ranges.length)
            return;

        // Remove the range and then apply to UI.
        const removedRange = this.ranges.splice(indexToDelete, 1)[0]; // [0] is because splice returns an array of removed values.
        this._applyRangesToUI(this.ranges);

        this._dispatchChangeEvent('deleted', indexToDelete, removedRange);
    }

    // -- DELEGATE HANDLERS --

    _handleClick(ev) {
        const target = ev.target;

        if (target.matches('.row-ispercent-percent') || target.matches('.row-ispercent-number') || target.matches('.row-ispercent'))
            this._handlePercentClick(target);
        else if (target.matches('.row-delete') || target.matches('.row-delete-icon'))
            this._handleDeleteClick(target);
        else if (target.matches('.adder') || target.matches('.adder-icon'))
            this._handleAdderClick(target);
    }
    _handleChange(ev) {
        const target = ev.target;

        if (target.matches('input.row-range-start'))
            this._handleRangeChange(target, 'start', 0);
        else if (target.matches('input.row-range-end'))
            this._handleRangeChange(target, 'end', null);
        else if (target.matches('.row-amount input'))
            this._handleAmountChange(target);
    }

    // -- ACTUAL HANDLERS --

    _handleAdderClick(target) {
        const index = this._getIndexForElement(target);

        if (!this.dispatchEvent(new CustomEvent('adding', { cancelable: true, detail: { index } })))
            return; // Cancelled.

        const isNewLastRow = index === this.ranges.length;
        const prevRange = index > 0 ? this.ranges[index - 1] : null;
        const nextRange = isNewLastRow ? null : this.ranges[index];

        // If we're adding a new row at the end, and there is an existing row and it has an infinite end point...
        // then need to modify the existing (current last) row so it has a non-infinite end value (don't want two infinite rows).
        if (isNewLastRow && prevRange && prevRange.end === null) {
            if (this.ranges.length > 1) {
                // Use the same range spread as the penultimate range's.
                const penultRange = this.ranges[this.ranges.length - 2];
                const penulSpread = penultRange.end - penultRange.start;
                prevRange.end = prevRange.start + penulSpread;
            }
            else {
                // Only the one range exists, so just use a default spread.
                prevRange.end = prevRange.start + RANGE_SPREAD_DEFAULT;
            }
        }

        const start = prevRange ? prevRange.end : 0;
        const end = nextRange ? nextRange.start : null; // If last then null (infinite), else the start of the range we're inserting before.
        const amount = (prevRange ? prevRange.amount : RANGE_AMOUNT_DEFAULT);
        const isPercent =
            prevRange
                ? prevRange.isPercent // Just use whatever the prev range has (if one exists),
                : (nextRange ? nextRange.isPercent : RANGE_ISPERCENT_DEFAULT);

        this.addRange(index, start, end, amount, isPercent); // Will update UI (for all ranges including prevRange).
    }
    _handleDeleteClick(target) {
        const index = this._getIndexForElement(target);

        if (!this.dispatchEvent(new CustomEvent('deleting', { cancelable: true, detail: { index } })))
            return; // Cancelled.

        // TODO: add an undo delete row which disappears after n-seconds
        this.delRange(index); // Will update UI.
    }
    _handlePercentClick(target) {
        if (this.disabled)
            return;

        const index = this._getIndexForElement(target);
        const range = this.ranges[index];

        // Update the isPercent UI and the range.isPercent data.
        range.isPercent = target.closest('.row-ispercent').classList.toggle(CLASS_ROW_PERCENT_ACTIVE);

        // Reformat the amount value.
        this.$.listing.querySelector(`.row[data-index="${index}"] .row-amount input`).value = this._formatNum(range.amount, range.isPercent);

        this._dispatchChangeEvent('isPercent', index, range);
    }

    _handleRangeChange(target, prop, defval) {
        const index = this._getIndexForElement(target);
        const range = this.ranges[index];

        range[prop] = this._roundNum(parseFloat(target.value) || defval);

        this._validateAndNormalizeRanges(this.ranges);
        this._applyRangesToUI(this.ranges);

        this._dispatchChangeEvent('range-' + prop, index, range);
    }
    _handleAmountChange(target) {
        const index = this._getIndexForElement(target);
        const range = this.ranges[index];

        range.amount = this._roundNum(parseFloat(target.value) || RANGE_AMOUNT_DEFAULT);
        target.value = this._formatNum(range.amount, range.isPercent);

        this._dispatchChangeEvent('amount', index, range);
    }

    // -- DATA MANAGING METHODS --

    _validateAndNormalizeRanges(ranges) {
        let prevRange = null;

        // Make sure the ranges are sorted correctly both for display but also
        // because the routine below assumes they are in order.
        ranges.sort((r1, r2) => {
            let diff = r1.start - r2.start; // Sort by range.start (which is by far the most important for the below logic)...
            if (!diff) diff = r1.end - r2.end; // Then if starts are the same sort by range.end because that seems to make sense...
            if (!diff) diff = r1.amount - r2.amount; // Finally we sort by range.amount though it's a bit goofy as values can be mixture of percent/money, but nothing else better to do.
            return diff;
        });

        ranges.forEach(currRange => {
            // Normalize falsy values and the below logic will adjust accordingly.
            currRange.start = currRange.start || 0;
            currRange.end = currRange.end || null; // null: inifnite
            currRange.amount = currRange.amount || 0;
            currRange.isPercent = (currRange.isPercent !== void 0) ? !!currRange.isPercent : RANGE_ISPERCENT_DEFAULT;

            // Start must be a number and must not be negative...
            if (typeof currRange.start !== 'number' || currRange.start < 0)
                currRange.start = 0;
            // End must be a number and must not be negative (though can be null)...
            if (typeof currRange.end !== 'number' || currRange.end < 0)
                currRange.end = null;
            // Amount must be a number.
            if (typeof currRange.amount !== 'number')
                currRange.amount = 0;

            if (prevRange != null) {
                // If previous end value is null (ie infinite) then the current range is invalid...
                if (prevRange.end === null) {
                    // We clear both start & end of the current range for good measure,
                    // start being null is important as it indicates invalidity
                    // (since start should not be null, whereas end can be when infinite).
                    currRange.start = null;
                    currRange.end = null;
                }
                else if (currRange.start < prevRange.end) {
                    // Current start must not be below previous end (though they can be the same)...
                    currRange.start = prevRange.end;
                }
            }

            // Current end must not be less than or equal to current start (unless the end is null, aka infinite)...
            if (currRange.end !== null && currRange.end <= currRange.start) {
                currRange.end = currRange.start + 1;
            }

            prevRange = currRange;
        });

        return ranges;
    }

    // -- UI UPDATING METHODS --

    _setDisabled(disabled) {
        // Some UI elements will be hidden by CSS automatically when [disabled] attribute exists,
        // and elements will have disabled bound in HTML on population,
        // however we need to update any existing elements to be disabled
        this.shadowRoot.querySelectorAll('input,button').forEach(ele => ele.disabled = disabled);

        // If disabled and no ranges then hide the heading also.
        this.$.heading.hidden = (this.disabled && (!this.ranges || !this.ranges.length));
    }

    _applyRangesToUI(ranges) {
        // We'll assemble the listing in a docfrag and then set it into the DOM.
        const docFrag = new DocumentFragment();

        if (ranges && ranges.length) {
            // If the first range's start is greater than zero then we need an adder before it.
            if (ranges[0].start > 0) {
                docFrag.appendChild(this._createAdder(0));
            }

            let prevRange;

            // Stamp the template for each of the data ranges.
            ranges.forEach((currRange, index) => {
                // If there is a gap between the previous range then we need to insert an adder.
                if (prevRange && prevRange.end < currRange.start) {
                    docFrag.appendChild(this._createAdder(index, true));
                }

                // Create the range/data row for the current loop.
                const row = this._createRow(index, currRange);

                // Always add the range/data row (in addition to any adder rows).
                docFrag.appendChild(row);

                prevRange = currRange;
            });
        }

        // Always add an adder at the end.
        docFrag.appendChild(this._createAdder(ranges.length));

        // Clear the listing and then add the newly generated rows.
        this.$.listing.innerHTML = '';
        this.$.listing.appendChild(docFrag);
    }

    _createRow(index, range) {
        this._cache.rowStamp = this._cache.rowStamp || templatize(this.$.rowTemplate);

        const row = new this._cache.rowStamp(Object.assign({ index, disabled: this.disabled }, range));

        return row.root;
    }
    _createAdder(index, isGap) {
        this._cache.adderStamp = this._cache.adderStamp || templatize(this.$.adderTemplate);

        const adder = new this._cache.adderStamp({ index, gapClass: isGap ? 'adder--gap' : '' }); // The index within the ranges array at which a new row would be inserted.

        return adder.root;
    }

    // -- HELPER METHODS --

    _roundNum(val) {
        if (val === null)
            return null;
        else
            return Math.round(val * ROUND_PRECISION) / ROUND_PRECISION;
    }
    _formatNum(val, isPercent) {
        if (val === void 0 || val === null)
            return '';
        else if (isPercent)
            return val.toString();
        else
            return val.toFixed(FORMAT_PRECISION);
    }

    _getIndexForElement(ele) {
        return parseInt(ele.closest('[data-index]').dataset['index']);
    }

    _dispatchChangeEvent(type, index, data) {
        this.dispatchEvent(new CustomEvent('change', { detail: { type, index, data } }));
    }

});